import PropertiesField from "./properties";
import PropertyField from "./property";
const slugify = require('slugify')

//-----------------------------------------------
// Total CMS Properties
//-----------------------------------------------
export default class SchemaPropertiesField extends PropertiesField {

    constructor(container, settings) {
		settings.fieldClass = "schema-field";
        super(container, settings);

        this.template  = this.container.querySelector(".schema-template");
        this.addButton = this.container.querySelector(".cms-add");
		this.addButton?.addEventListener("click", this.addTemplate.bind(this));

		// Listen for override button clicks from inherited properties
		this.initOverrideButtons();
    }

	initOverrideButtons() {
		document.querySelectorAll('.override').forEach(button => {
			button.addEventListener('click', (e) => {
				const name = button.dataset.propertyName;
				const definition = JSON.parse(button.dataset.propertyDefinition);
				this.overrideProperty(name, definition);
			});
		});
	}

	overrideProperty(name, definition) {
		const clone = this.template.content.cloneNode(true);
		const parent = this.addButton.parentNode;
		parent.insertBefore(clone, this.addButton);

		const field = Array.from(parent.querySelectorAll("." + this.fieldClass)).pop();

		// Mark as new property for styling
		field.classList.add('new-property');

		// Populate the property name
		const nameInput = field.querySelector('input[type="text"]');
		if (nameInput) {
			nameInput.value = name;
		}

		// Populate the field type dropdown (uses name="field" attribute)
		const fieldSelect = field.querySelector('[name=field]');
		if (fieldSelect && definition.field) {
			fieldSelect.value = definition.field;
			// Trigger change event to update UI
			fieldSelect.dispatchEvent(new Event('change', { bubbles: true }));
		}

		// Populate the type dropdown (uses name="type" attribute)
		const typeSelect = field.querySelector('[name=type]');
		if (typeSelect && definition.type) {
			typeSelect.value = definition.type;
			typeSelect.dispatchEvent(new Event('change', { bubbles: true }));
		}

		// Populate text fields
		this.populateFieldValue(field, 'label', definition.label);
		this.populateFieldValue(field, 'help', definition.help);
		this.populateFieldValue(field, 'placeholder', definition.placeholder);
		this.populateFieldValue(field, 'default', definition.default);
		this.populateFieldValue(field, 'factory', definition.factory);

		// Populate select fields. Accept canonical `schemaref` and the legacy `deckref` alias.
		this.populateSelectValue(field, 'schemaref', definition.schemaref ?? definition.deckref);

		// Populate JSON fields (settings, options, extra)
		this.populateJsonField(field, 'settings', definition.settings);
		this.populateJsonField(field, 'options', definition.options);

		// Handle extra properties (anything not in standard schema fields)
		const extraProps = this.extractExtraProperties(definition);
		if (Object.keys(extraProps).length > 0) {
			this.populateJsonField(field, 'extra', extraProps);
		}

		// Initialize the new field
		this.newField(field);

		// Update the container's CSS classes to match the select values
		if (field.totalfield && typeof field.totalfield.updateIcons === 'function') {
			field.totalfield.updateIcons();
		}

		// Scroll to the new field if not in view
		this.scrollToField(field);
	}

	populateFieldValue(field, fieldName, value) {
		if (!value) return;

		const input = field.querySelector(`[name="${fieldName}"], [name*="${fieldName}"], [data-field="${fieldName}"]`);
		if (input) {
			input.value = value;
		}
	}

	populateSelectValue(field, fieldName, value) {
		if (!value) return;

		const select = field.querySelector(`select[name="${fieldName}"]`);
		if (select) {
			select.value = value;
			select.dispatchEvent(new Event('change', { bubbles: true }));
		}
	}

	populateJsonField(field, fieldName, value) {
		if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) return;

		const input = field.querySelector(`[name="${fieldName}"], [data-field="${fieldName}"]`);
		if (input) {
			// Convert object/array to JSON string for textarea fields
			input.value = typeof value === 'object' ? JSON.stringify(value, null, 2) : value;
		}
	}

	extractExtraProperties(definition) {
		// Standard schema property fields that should not be in extra
		const standardFields = [
			'field', 'type', 'label', 'help', 'placeholder', 'default',
			'factory', 'schemaref', 'deckref', 'settings', 'options', '$ref'
		];

		const extra = {};
		for (const key in definition) {
			if (!standardFields.includes(key)) {
				extra[key] = definition[key];
			}
		}
		return extra;
	}

	scrollToField(field) {
		// Small delay to ensure the field is fully rendered
		setTimeout(() => {
			const rect = field.getBoundingClientRect();
			const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;

			if (!inView) {
				field.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}, 100);
	}

    addTemplate() {
		const clone = this.template.content.cloneNode(true);
		const parent = this.addButton.parentNode;
		parent.insertBefore(clone, this.addButton);

		const field = Array.from(parent.querySelectorAll("."+this.fieldClass)).pop();

		// Mark as new property for styling
		field.classList.add('new-property');

		field.querySelector("input").focus();
		this.newField(field);
	}

    newField(field) {
		const newField = new PropertyField(field, this.fieldClass);
		newField.form = this.form;
		this.initActionbar(field);
		this.form.processFields();
	}

	initActionbar(field) {
		const trash     = field.querySelector("button.trash");
		const duplicate = field.querySelector("button.duplicate");
		const property  = field.querySelector("input");

		// Ensure properties are all lowercase and valid Twig variable names
		property?.addEventListener("change", () => {
			// Replace hyphens with underscores because it makes for nicer twig macros
			property.value = slugify(property.value, { lower: true }).replace(/-/g, "_").replace(/^[0-9_]+/, "");
		});

		trash?.addEventListener("click", () => this.removeField(field));
		duplicate?.addEventListener("click", () => this.duplicateField(field));
	}

	removeField(field) {
		field.remove();
	}

	duplicateField(field) {
		// Capture CodeMirror values by textarea name — CM6 editor state
		// does not survive cloneNode.
		const editorValues = new Map();
		field.querySelectorAll('.cm-editor').forEach(wrapper => {
			if (!wrapper._totalcmsEditor) return;
			// The textarea is a sibling of the editor's container div
			const container = wrapper.parentElement;
			const textarea = container?.previousElementSibling;
			if (textarea && textarea.tagName === 'TEXTAREA' && textarea.name) {
				editorValues.set(textarea.name, wrapper._totalcmsEditor.getValue());
			}
		});

		// Get all select values before cloning
		const selects = field.querySelectorAll('select');
		const selectValues = Array.from(selects).map(select => select.value);

		const clone = field.cloneNode(true);
		const parent = field.parentNode;
		parent.insertBefore(clone, field.nextSibling);

		const newField = field.nextSibling;

		// Remove cloned editor wrappers so fields re-init cleanly
		newField.querySelectorAll('.cm-editor').forEach(wrapper => wrapper.remove());
		// Also remove cloned editor containers
		newField.querySelectorAll('.totalform-json-editor-container').forEach(c => c.remove());

		// Restore captured editor values onto the cloned textareas
		editorValues.forEach((value, name) => {
			const textarea = newField.querySelector(`textarea[name="${name}"]`);
			if (textarea) textarea.value = value;
		});

		// Restore select values after cloning
		const clonedSelects = newField.querySelectorAll('select');
		clonedSelects.forEach((select, index) => {
			if (selectValues[index]) {
				select.value = selectValues[index];
			}
		});

		newField.querySelector("input").focus();
		this.newField(newField);
	}

	schema() {
        return {
            type     : "schemaProperties",
            fieldset : this.type
        };
    }
}
